import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { Recording, PaginatedResponse, ApiResponse } from 'types';
import { mockRecordings } from 'mocks/mockServer';
import { isCdnRecordingUrl, isSignedTokenRecordingUrl } from 'utils/recordingHosts';

const useMock = process.env.REACT_APP_USE_MOCK === 'true';
// Host that serves recording paths when the backend returns a RELATIVE one.
// A single unified portal serves more than one region, so this must not be a
// hard-coded tvoice host — it is per-deployment configuration. The tvoice
// value stays as the default so the Africa/Europe builds behave as before.
const cdnBaseUrl = process.env.REACT_APP_RECORDING_CDN_URL || 'https://recording-tvoice.teeoff.tech';

export const recordingsService = {
  async getRecordings(params?: any): Promise<ApiResponse<PaginatedResponse<Recording>>> {
    if (useMock) {
      return mockRecordings.getRecordings(params);
    }

    // Convert frontend 1-based page to backend 0-based page
    const apiParams: any = { ...params };
    if (params?.page !== undefined) {
      apiParams.page = Math.max(0, params.page - 1);
    }

    try {
      const response = await axiosInstance.get<ApiResponse<PaginatedResponse<Recording>>>(
        API_ENDPOINTS.RECORDINGS,
        { params: apiParams }
      );

      // API returns wrapped response: { success, message, data }
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch recordings'
      };
    }
  },

  async downloadRecording(id: string): Promise<Blob> {
    const response = await axiosInstance.get(
      API_ENDPOINTS.RECORDING_DOWNLOAD(id),
      { responseType: 'blob' }
    );

    return response.data;
  },

  /**
   * A URL this user can actually play, for one call.
   *
   * The recording is NOT fetched from the third-party host any more. That host
   * answers `WWW-Authenticate: Basic` and refuses our JWT — as a query
   * parameter and as a bearer header alike — so the old
   * getAuthenticatedRecordingUrl below could never have produced a playable
   * link for it, and putting the Basic credential here would hand every
   * operator every tenant's recordings.
   *
   * cpass now keeps its own copy under a key containing the organisation, and
   * signs a short-lived URL for it. Being signed in is not enough: the key is
   * built from the caller's own organisation, so another tenant's call id
   * resolves to nothing rather than to their audio.
   *
   * Returns null when there is no recording for the call, which is an ordinary
   * outcome and not an error — the player is simply not offered.
   */
  async getPlayableUrl(callId: string, organizationId?: string): Promise<string | null> {
    const res = await axiosInstance.get(
      `/calls/${encodeURIComponent(callId)}/recording`,
      { params: organizationId ? { organizationId } : {} },
    );
    const data = (res.data && (res.data.data ?? res.data)) || {};
    return data.available && data.url ? String(data.url) : null;
  },

  /**
   * The URL to actually play for one call, trying both mechanisms in order.
   *
   * Neither one works everywhere, which is why this exists rather than a single
   * call. The presigned URL is preferred because its object key contains the
   * caller's own organisation, so another tenant's call id resolves to nothing
   * instead of to their audio — but it only exists where cpass holds a copy and
   * the /calls/{id}/recording endpoint is deployed. The older backends have
   * neither, and there the stored recording URL with the session token appended
   * is what plays.
   *
   * Returning null means no recording, which is an ordinary outcome — many
   * calls have none — and the caller should say so rather than show a player.
   */
  async resolvePlayableUrl(
    callId: string | null | undefined,
    recordingUrl: string | null | undefined,
    organizationId?: string,
  ): Promise<string | null> {
    const cdnFirst = isCdnRecordingUrl(recordingUrl);

    // THE CDN FIRST when the stored URL is one — that is the mechanism the
    // tvoice portals actually run on, and asking cpass first there costs a
    // round trip that always fails before the working path is tried.
    if (cdnFirst && recordingUrl) {
      try {
        const direct = this.getAuthenticatedRecordingUrl(recordingUrl);
        if (direct) return direct;
      } catch {
        // No session token — fall through and let cpass try.
      }
    }

    if (callId) {
      try {
        const signed = await this.getPlayableUrl(String(callId), organizationId);
        if (signed) return signed;
      } catch {
        // Endpoint absent or errored — that is precisely when the fallback matters.
      }
    }

    // A NON-CDN STORED URL IS NOT OFFERED AT ALL.
    //
    // It used to be returned here as a last resort with the session token
    // appended. On the Green Giraffe estate recording_url points at the SWITCH,
    // which does not understand our JWT: it answers `WWW-Authenticate: Basic`,
    // and the browser responds by popping a username/password box at the
    // operator. A credentials prompt is worse than a refusal — it looks like the
    // app is asking them to log in again, and no credential they have would
    // work, because the one that does is a server-side secret that must never
    // reach a browser.
    //
    // So this returns null and the caller says "no recording is held for this
    // call", which is the truth until cpass holds its own copy. THE REAL FIX IS
    // `recording.store.enabled`: it defaults to false and is not set on the
    // running task, so /calls/{id}/recording always answers "not available" and
    // every switch-hosted recording falls to this branch. Turning the store on
    // makes the signed-copy path above succeed and this branch stop mattering.
    return null;
  },

  /**
   * The URL to SAVE for one call — the same audio the player gets, asked for as
   * a download.
   *
   * <p>It resolves through {@link resolvePlayableUrl} rather than taking the
   * stored `recording_url` and appending the session token, which is what the
   * download buttons used to do and why none of them worked here. On this
   * estate `recording_url` names `recordings.greengiraffe.in`, whose CloudFront
   * function wants the short-lived token cpass mints as `?t=` — it reads
   * `?token=` as no token at all and answers 401. Playback had already moved to
   * the minted URL; download never followed, so a call you could listen to was
   * a call you could not save.
   *
   * <p>Returns null when there is no recording, which is an ordinary outcome:
   * the caller should say so rather than offer a broken link.
   */
  async resolveDownloadUrl(
    callId: string | null | undefined,
    recordingUrl: string | null | undefined,
    organizationId?: string,
  ): Promise<string | null> {
    const url = await this.resolvePlayableUrl(callId, recordingUrl, organizationId);
    if (!url) return null;

    const sep = url.includes('?') ? '&' : '?';
    if (isSignedTokenRecordingUrl(url)) {
      // `dl` survives the viewer-REQUEST function, which strips only `t`, and is
      // read by the viewer-RESPONSE function that sets `content-disposition:
      // attachment`. Without it the browser treats a cross-origin link as a
      // navigation — the `download` attribute is ignored off-origin — and plays
      // the wav in a tab instead of saving it.
      return `${url}${sep}dl=1`;
    }
    return `${url}${sep}download=true`;
  },

  /**
   * Hand an already-resolved URL to the browser as a save.
   *
   * <p>The `download` attribute is ADVISORY here and mostly ignored: browsers
   * honour it only for same-origin, blob: and data: URLs, and a recording is
   * none of those. What actually forces the save, and names the file, is the
   * `content-disposition` the CDN returns for `?dl=1`. The attribute stays as
   * the fallback for the same-origin case.
   */
  startDownload(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    // Appended on purpose: Firefox ignores click() on an anchor that is not in
    // the document, so the previous version saved nothing at all there.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },

  getAuthenticatedRecordingUrl(recordingUrl: string): string {
    // Get token from storage (using 'auth_token' key from storage.ts)
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Append token as query parameter
    const separator = recordingUrl.includes('?') ? '&' : '?';
    return `${recordingUrl}${separator}token=${encodeURIComponent(token)}`;
  },
  getAuthenticatedDownloadUrl(recordingPath: string): string {
    // The key is 'auth_token' — the one storage.ts writes. This read used to be
    // 'jwt_token', a key nothing in the codebase ever sets, so every download
    // threw "No authentication token found" before reaching the network. The
    // Africa/Europe line fixed this in Nov 2025; it was never brought across.
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    // An absolute URL from the backend is used as given — only a relative path
    // gets resolved against the configured recording host.
    const isAbsolute = /^https?:\/\//i.test(recordingPath);
    const url = isAbsolute
      ? new URL(recordingPath)
      : new URL(this.parseRecordingPath(recordingPath), cdnBaseUrl);

    url.searchParams.append('token', token);
    // Force a download rather than streaming it inline.
    url.searchParams.append('download', 'true');

    return url.toString();
  },
  /**
   * Direct download using browser's native download
   * This avoids timeout issues and shows browser's download progress
   */
  downloadRecordingDirect(recordingPath: string, filename?: string): void {
    try {
      const downloadUrl = this.getAuthenticatedDownloadUrl(recordingPath);
      
      // Create temporary anchor element
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename || this.extractFilename(recordingPath);
      a.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to initiate download:', error);
      throw error;
    }
  },
  
  /**
   * Alternative: Open in new tab (lets browser handle download)
   */
  downloadInNewTab(recordingPath: string): void {
    const downloadUrl = this.getAuthenticatedDownloadUrl(recordingPath);
    window.open(downloadUrl, '_blank');
  },
  
  /**
   * Helper: Extract filename from path
   */
   extractFilename(path: string): string {
    const urlPath = this.parseRecordingPath(path);
    const parts = urlPath.split('/');
    return parts[parts.length - 1] || 'recording.wav';
  },
  
  /**
   * Helper: Parse recording path from full URL
   */
   parseRecordingPath(fullUrl: string): string {
    try {
      const url = new URL(fullUrl);
      return url.pathname;
    } catch {
      // Already a path
      return fullUrl;
    }
  }
};

